/**
 * PreviewTable — "Review contacts" step of Combine & Filter (prototype v39
 * `#page-combine-filter`, `renderCombinePick`).
 *
 * A checked row (Included view) is KEPT; unticking cherry-picks it OUT
 * (`excluded_contact_ids` — the recipe's own field, capped at 100, WAF 8 KB
 * body-cap reasoning, see `CombineRecipe.excluded_contact_ids` in
 * `schemas.py`). At the cap, further unticking is BLOCKED at the state layer
 * (`CombineFilterPage.toggleExcluded` silently no-ops past the cap) — the
 * banner below says so; the checkbox itself no longer greys out per row
 * (round 2 tradeoff, see the docblock on the table body below).
 *
 * EVERY item the response returns is rendered, including one the recruiter
 * already unticked: the preview response RETURNS those rows flagged
 * `excluded: true` rather than dropping them (iter-1 review, finding C-I1) —
 * before that fix, a row a recruiter had just unticked vanished from the very
 * next preview refetch (its id was already in `excluded_contact_ids`) with no
 * way to bring it back short of reloading the page. Whether a row reads as
 * excluded comes from the page's own `excludedIds` alone: the server's flag
 * only echoes the ids the page sent, and reading it too kept a re-ticked row
 * unticked until the next response landed.
 *
 * ROUND 2 (sub-task R2-B): the table BODY is now the SAME `ContactsTable` the
 * People-list detail page renders (`variant="preview"`, `richPreview`), via
 * the pure adapter `toContactRow` — avatar+name, location, LinkedIn, Thrive
 * (avatar + linked id, or "Not checked"/"Not found"), Lists, and
 * server-side sorting by clicking the Name / Location / Thrive headers
 * (or the Sort picker, which also reaches Company and Position; the Thrive
 * sort orders by Thrive status, then Thrive ID). The checkbox
 * column DOUBLES as Restore on the Excluded tab: "checked" always means
 * "keep this person in the result", so ticking an excluded row's box IS
 * restoring it — there is no separate Restore button, since `ContactsTable`
 * renders one selection column, not a per-variant action slot. The Excluded
 * tab still lists ONLY the rows this page carries `excluded === true`
 * ("Removed by you" — manual exclusions; filtered/deduped/already-in-Thrive
 * rows never reach this response at all, so they cannot leak in here).
 * Bug hunt 2026-09-23 (F4): that tab is its own server-side view
 * (`CombinePreviewRequest.view = "excluded"`), so it lists EVERY manual
 * exclusion across pages rather than only the ones on the Included tab's
 * current page. `view` is owned by `CombineFilterPage` (it is part of the
 * recipe); the pager's `total` is the server's count for that view (F3).
 *
 * A header search box (client-side, over the CURRENT PAGE's items only —
 * this table has no server-side text search of its own) and a segmented
 * Included / Excluded view, mirroring `renderCombinePick`'s
 * `combinePickView`. Both counts in the segment labels (`funnel.result` /
 * `funnel.excluded`) are the FULL recipe's counts, not this page's.
 */
import { SegmentedControl } from "@nu-assist/ui";
import { useMemo, useState } from "react";

import type { CombinePreviewView } from "@nu-assist/contracts";

import type {
  CombineFunnel,
  CombinePreviewContact,
  ImportsClient,
} from "../../../../clients/imports.client";
import { TablePagination } from "../../../../components/TablePagination";
import {
  ContactsTable,
  type ContactsTableSort,
  NAME_SORT_KEY,
} from "../../import-detail/ContactsTable";
import { toContactRow } from "../../import-detail/toContactRow";
import { useResolveAvatars } from "../../import-detail/useResolveAvatars";

export interface PreviewTableProps {
  client: ImportsClient;
  items: readonly CombinePreviewContact[];
  /** Only `result` / `excluded` are read — the segmented control's counts. */
  funnel: CombineFunnel;
  page: number;
  pageSize: number;
  /** `CombinePreviewResponse.total` — how many rows the ACTIVE `view` pages
   *  over (the pager's count), not `funnel.result`. */
  total: number;
  onPageChange: (page: number) => void;
  /** Included / "Removed by you" — part of the preview request, so the page
   *  owns it and resets `page` when it changes. */
  view: NonNullable<CombinePreviewView>;
  onViewChange: (view: NonNullable<CombinePreviewView>) => void;
  excludedIds: ReadonlySet<string>;
  onToggleExcluded: (contactId: string) => void;
  maxExclusions: number;
  /** Winning list id -> short (extension-stripped) title, for the "Lists"
   *  column. A missing entry (a source the page has not fetched into its
   *  local picker state) renders the raw id. */
  sourceTitleById: ReadonlyMap<string, string>;
  /** Server-side sort state + setter — forwarded straight to `ContactsTable`;
   *  `CombineFilterPage` maps the sort keys `ContactsTable` produces
   *  (`NAME_SORT_KEY`, `"location"`) onto `CombinePreviewRequest.sort_key`. */
  sort?: ContactsTableSort | null;
  onSortChange?: (key: string) => void;
  /** Whether ANY selected source has a usable Compare state for the active
   *  mode (`preview.sources.some((s) => s.checked)`) — drives the Thrive ID
   *  header's own "has Compare run" wording, same prop the detail page
   *  passes. `false`/omitted (nothing checked yet) keeps every row's header
   *  reading "Compare to Thrive has not been run for this list". */
  compareHasRun?: boolean;
}

function fullName(item: CombinePreviewContact): string {
  return [item.first_name, item.last_name].filter(Boolean).join(" ") || "(no name)";
}

function matchesQuery(item: CombinePreviewContact, q: string): boolean {
  if (!q) return true;
  const haystack = [
    fullName(item),
    item.current_position,
    item.headline,
    item.current_company,
    item.location,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

export function PreviewTable({
  client,
  items,
  funnel,
  page,
  pageSize,
  total,
  onPageChange,
  view,
  onViewChange,
  excludedIds,
  onToggleExcluded,
  maxExclusions,
  sourceTitleById,
  sort,
  onSortChange,
  compareHasRun,
}: PreviewTableProps) {
  const atCap = excludedIds.size >= maxExclusions;
  const [query, setQuery] = useState("");

  function isExcluded(item: CombinePreviewContact): boolean {
    return excludedIds.has(item.import_contact_id);
  }

  const q = query.trim().toLowerCase();
  // The server already scoped `items` to `view`; on "Removed by you" a row
  // the recruiter just ticked back in stays listed (checked) until the next
  // response drops it, so the click visibly lands. Cheap over a page-sized
  // array (<= a few dozen rows) — no memo needed.
  const shown = items.filter((item) => matchesQuery(item, q));

  const rows = useMemo(() => shown.map(toContactRow), [shown]);
  // Epic 2026-09-28-combine-perf-batch-1, sub-task 02 (item 3): avatars are
  // presigned from the PAGE's full `items`, not from `shown` (the "Find a
  // contact…" client-side filter's subset of the SAME page). `shown` — and
  // therefore `rows` — gets a NEW array on every keystroke, which used to
  // feed `useResolveAvatars` directly; narrowing the visible rows narrows
  // the set of `profile_image_s3_key`s too, which changed the presign
  // query's key and re-fired `POST /avatars/presign` on each keystroke.
  // `pageRows` only changes when `items` itself changes (a real page turn or
  // a fresh preview), so presigning happens once per page load — the
  // resolved map is keyed by S3 key, so it stays correct for whichever
  // subset the search box is currently showing.
  const pageRows = useMemo(() => items.map(toContactRow), [items]);
  const resolvedAvatars = useResolveAvatars(client, pageRows);
  // A result with Thrive candidacy stages (the "Candidacy stage" filter is
  // on, 05) shows them in a Stage column.
  const showStageColumn = items.some((item) => item.candidacy_stage);

  // "Checked" always means "keep this person" — see the docblock. On the
  // Included tab that is every shown row not (yet) excluded; on the Excluded
  // tab every shown row IS excluded, so nothing starts checked and ticking
  // one restores it. No `useMemo` — cheap over a page-sized array, same
  // reasoning as `shown` above; memoizing would have to list `isExcluded`
  // (redefined every render) as a dependency anyway.
  const keptIds = new Set(
    shown.filter((item) => !isExcluded(item)).map((item) => item.import_contact_id)
  );

  const keptShownCount = keptIds.size;
  const allShownKept = shown.length > 0 && keptShownCount === shown.length;

  function toggleAllShown() {
    // Mirrors the old `setCombinePickAll`: if every shown row is kept, the
    // header action unticks them all; otherwise it keeps them all. A
    // cap-blocked toggle is a silent no-op per row, same as clicking that
    // row's own checkbox at the cap (`CombineFilterPage.toggleExcluded`).
    for (const item of shown) {
      const excluded = isExcluded(item);
      if (allShownKept && !excluded) onToggleExcluded(item.import_contact_id);
      else if (!allShownKept && excluded) onToggleExcluded(item.import_contact_id);
    }
  }

  return (
    <div data-testid="combine-preview-table" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a contact…"
          aria-label="Find a contact"
          data-testid="combine-preview-search"
          className="min-w-48 flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
        />
        {onSortChange ? (
          // Company and Position render INSIDE the Name cell (no header of
          // their own), so they get this picker; Name / Location / Thrive
          // also stay sortable from their headers. Re-picking the active key
          // is a no-op here — direction flips from the column header.
          <select
            aria-label="Sort contacts by"
            data-testid="combine-preview-sort"
            value={sort?.key ?? NAME_SORT_KEY}
            onChange={(e) => {
              if (e.target.value !== (sort?.key ?? NAME_SORT_KEY)) onSortChange(e.target.value);
            }}
            className="rounded-md border border-border bg-bg px-2 py-1.5 text-body text-text focus:border-accent focus:outline-none"
          >
            <option value={NAME_SORT_KEY}>Sort: Name</option>
            <option value="company">Sort: Company</option>
            <option value="position">Sort: Position</option>
            <option value="location">Sort: Location</option>
            <option value="thrive">Sort: Thrive status</option>
          </select>
        ) : null}
        <SegmentedControl
          aria-label="Show included or excluded contacts"
          data-testid="combine-preview-view"
          value={view}
          onChange={onViewChange}
          options={[
            {
              value: "included",
              label: `Included (${funnel.result.toLocaleString()})`,
              testId: "combine-preview-view-included",
            },
            {
              value: "excluded",
              label: `Removed by you (${funnel.excluded.toLocaleString()})`,
              testId: "combine-preview-view-excluded",
            },
          ]}
        />
      </div>

      {view === "included" && atCap ? (
        <p
          data-testid="combine-preview-exclusion-cap"
          className="rounded-md border border-warning bg-warning-muted px-3 py-1.5 text-caption text-warning-ink"
        >
          You've reached the limit of {maxExclusions} exclusions. Untick fewer people, or narrow
          with a filter instead.
        </p>
      ) : null}

      {shown.length === 0 ? (
        <p className="text-body text-text-muted" data-testid="combine-preview-empty">
          {view === "excluded" ? "Nobody excluded." : "No matches."}
        </p>
      ) : (
        <div className="rounded-md border border-border">
          {/* Header row's own "select all shown" checkbox — `ContactsTable`'s
              own header checkbox already calls `onToggleSelectAll`, so this
              is wired straight through rather than duplicated. */}
          <ContactsTable
            variant="preview"
            richPreview
            showSelectionColumn
            dimUnselectedRows
            contacts={rows}
            columns={[]}
            resolvedAvatars={resolvedAvatars}
            nameQuery={query}
            previewStageColumn={showStageColumn}
            sourceListsFor={(row) => {
              const item = shown.find((i) => i.import_contact_id === row.import_contact_id);
              if (!item) return undefined;
              return item.source_import_ids.map((id) => ({
                id,
                title: sourceTitleById.get(id) ?? id,
              }));
            }}
            selectedIds={keptIds}
            selectionMode="explicit"
            onToggleSelect={(id) => onToggleExcluded(id)}
            onToggleSelectAll={() => toggleAllShown()}
            sort={sort}
            onSort={onSortChange}
            compareHasRun={compareHasRun}
          />
        </div>
      )}
      <TablePagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        noun="person"
        nounPlural="people"
        testId="combine-preview-pagination"
      />
    </div>
  );
}
